import { useMemo } from 'react';
import {
  Card,
  CardBody,
  CardFooter,
  CardItem,
  type CascadeOption,
} from 'components/Card';
import { OrganizationNodesAndRoles } from 'components/OrganizationNodeAndRoles';
import { OrganizationTreeSelect } from 'components/OrganizationTreeSelect';
import { get, startCase } from 'lodash';
import {
  assignableAppPermissionsSelector,
  assignableScopesSelector,
} from 'slices/assignablePermission.slice';
import {
  appPermissionsSelector,
  scopesSelector,
} from 'slices/permission.slice';
import { useAppSelector } from 'slices/store';
import {
  APP_ID_TO_NAME_MAP,
  type EAppId,
} from '@timeedit/types/lib/enums/appIds.enum';
import type { TUserProfile } from '@timeedit/types/lib/types';
import './PermissionsAndOrgs.scss';

type IOption = {
  value: string;
  label: string;
  disabled: boolean;
  key: string;
};

type PermissionsAndOrgsProps = {
  profile?: TUserProfile;
  onChange: (key: string, value: any) => void;
};

const unassignableScopes = new Set(['TE_VIEWER::readOnly']);

export const PermissionsAndOrgs = ({
  profile,
  onChange,
}: PermissionsAndOrgsProps) => {
  const allScopes = useAppSelector(scopesSelector);
  const allAppPermissions = useAppSelector(appPermissionsSelector);
  const assignableAppPermissions = useAppSelector(
    assignableAppPermissionsSelector
  );
  const assignableScopes = useAppSelector(assignableScopesSelector);

  /**
   * Helper function to create IOption objects to be used by virtual list.
   * @param {string} value - The value of the option.
   * @param {any[]} assignableValues - The list of assignable values.
   * @returns {IOption} The created option object.
   */
  const createOption = (value: string, assignableValues: any[]): IOption => ({
    value,
    label: value,
    key: value,
    disabled: !assignableValues.map((item) => item.value).includes(value),
  });

  const appNameMap = useMemo(() => {
    const map: { [key: string]: string } = {};
    Object.keys(APP_ID_TO_NAME_MAP).forEach((key) => {
      map[key] =
        APP_ID_TO_NAME_MAP[key as EAppId] ||
        startCase(key.replace(/TE_/g, '').toLowerCase());
    });
    return map;
  }, []);

  // Prepare the profile's scopes, app permissions, and organization nodes and roles for display in the virtual list
  const formattedUserScopes: IOption[] = useMemo(() => {
    return (
      profile?.scopes?.map((scope) => createOption(scope, assignableScopes)) ||
      []
    );
  }, [profile, assignableScopes]);

  const formattedUserPermissions: IOption[] = useMemo(() => {
    return (
      profile?.appPermissions?.map((permission) =>
        createOption(permission, assignableAppPermissions)
      ) || []
    );
  }, [profile, assignableAppPermissions]);

  const isScopeAssignable = (scope: string) => {
    // Check if the scope is part of the set of unassignable scopes
    if (unassignableScopes.has(scope)) {
      return false;
    }

    // Return false if the scope is part of the profile's scopes
    if (formattedUserScopes.some((us) => us.value === scope)) {
      return false;
    }

    // Check if the scope is part of the assignable scopes
    return assignableScopes.some((as) => as.value === scope);
  };

  // Prepare scopes and app permissions for display in the cascade menu
  const formattedScopes: CascadeOption[] = Object.values(
    allScopes.reduce<Record<string, CascadeOption>>((acc, scope) => {
      const [app, level] = scope.value.split('::');

      acc[app] = {
        value: app,
        label: appNameMap[app] || startCase(app.toLocaleLowerCase()),
        children: [
          ...(acc[app]?.children || []),
          {
            value: scope.value,
            label: level,
            disabled: !isScopeAssignable(scope.value),
          },
        ],
      };

      return acc;
    }, {})
  );

  const formattedAppPermissions: CascadeOption[] = Object.values(
    allAppPermissions.reduce((acc, appPermission) => {
      const [app, level] = appPermission.value.split('::');
      if (!level) return acc;
      return {
        ...acc,
        [app]: {
          value: app,
          label: appNameMap[app],
          children: [
            ...(acc[app]?.children || []),
            {
              value: appPermission.value,
              label: level,
              disabled:
                assignableAppPermissions.findIndex(
                  (assignableAppPermission) =>
                    assignableAppPermission.value === appPermission.value
                ) === -1 ||
                formattedUserPermissions.findIndex(
                  (userPermission) =>
                    userPermission.value === appPermission.value
                ) !== -1,
            },
          ],
        },
      };
    }, [])
  );

  const updatePermissions = (
    type: 'scopes' | 'appPermissions',
    value: string,
    operation: 'add' | 'delete'
  ) => {
    if (!type) return;
    const currentPermissions = get(profile, type, []);
    let updatedPermissions: string[] = [];
    if (operation === 'add') {
      updatedPermissions = [...currentPermissions, value];
    } else if (operation === 'delete') {
      updatedPermissions = currentPermissions.filter(
        (permission: string) => permission !== value
      );
    }
    onChange(type, updatedPermissions);
  };

  return (
    <div>
      <OrganizationTreeSelect
        label="Standard Organization"
        value={profile?.standardOrganization}
        onChange={(newStdOrg) => {
          onChange('standardOrganization', newStdOrg);
        }}
        allowClear
        tooltip="Standard organization"
        placeholder="Select"
        dataTestId="standard-org"
        onlyLeafNodes
      />
      <div className="permissions-orgs">
        <Card
          title="Scopes"
          footer={
            <CardFooter.CascadeMenu
              options={formattedScopes}
              onClick={(newScope) =>
                updatePermissions('scopes', newScope, 'add')
              }
            />
          }
        >
          <CardBody.VirtualList data={formattedUserScopes}>
            {(option: IOption) => (
              <CardItem.TextItem
                title={option.value}
                onDelete={(e: string) =>
                  updatePermissions('scopes', e, 'delete')
                }
              />
            )}
          </CardBody.VirtualList>
        </Card>
        <Card
          title="App permissions"
          footer={
            <CardFooter.CascadeMenu
              options={formattedAppPermissions}
              onClick={(newPermission) =>
                updatePermissions('appPermissions', newPermission, 'add')
              }
            />
          }
        >
          <CardBody.VirtualList data={formattedUserPermissions}>
            {(option: IOption) => (
              <CardItem.TextItem
                title={option.value}
                onDelete={(e: string) =>
                  updatePermissions('appPermissions', e, 'delete')
                }
              />
            )}
          </CardBody.VirtualList>
        </Card>
        <OrganizationNodesAndRoles
          title="Organization nodes and roles"
          tooltip="Organization nodes and roles"
          organizationNodesAndRoles={profile?.organizationsAndRoles ?? []}
          onChange={(value) => onChange('organizationsAndRoles', value)}
          key={profile?.id}
        />
      </div>
    </div>
  );
};
